/** URL helper utilities for extracting and formatting URL information */

/**
 * Extract base domain from a full URL
 *
 * @param url Full URL (e.g., "https://www.nytimes.com/article/foo")
 * @returns Base domain (e.g., "nytimes.com") or null if invalid
 */
export const getBaseUrlFromLink = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    // Remove 'www.' prefix if present for cleaner display
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    // If URL parsing fails, return null
    return null;
  }
};

/**
 * The URL to open for an external (web/custom) citation, or null when it is not
 * an absolute http(s) URL.
 *
 * `window.open` resolves anything else (scheme-less "www.site.com/x", relative
 * "/x", "#") against the current origin, so a bad citation URL would open
 * PRATUS itself in a new tab — and non-http schemes (javascript:, data:) must
 * never be opened at all. Returned unchanged so the user opens exactly what the
 * source recorded.
 */
export const getExternalHttpUrl = (url: unknown): string | null => {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? trimmed
      : null;
  } catch {
    return null;
  }
};

const TEAMS_DEEP_LINK_ROUTES = [
  /^\/iap\/[0-9a-f-]{36}\/(dashboard|files)$/i,
  /^\/sitreps\/[0-9a-f-]{36}$/i,
  /^\/org_files$/,
];

/**
 * The in-app route for a Teams tab deep link (`context.page.subPageId`), or
 * null unless it exactly matches an allowlisted route, so it can never redirect
 * off-site (absolute URLs, `//host`, query strings and other pages are
 * ignored).
 */
export const getTeamsDeepLinkPath = (subPageId: unknown): string | null =>
  typeof subPageId === 'string' &&
  TEAMS_DEEP_LINK_ROUTES.some((route) => route.test(subPageId))
    ? subPageId
    : null;
